import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Lottie from 'lottie-react';
import homeAnimationData from './icons/home.json';
import productsAnimationData from './icons/Product.json';
import categoriesAnimationData from './icons/categories.json';
import storeAnimationData from './icons/store.json';
import aboutAnimationData from './icons/about.json';
import blogAnimationData from './icons/Blog.json';
import businessAnimationData from './icons/business.json';
import moreAnimationData from './icons/more.json';
import './NewHeroHeader.css';
import FlipText from './FlipText';

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif";

const deviceTypes = [
    { name: 'Laptops', icon: '💻', link: '/laptops' },
    { name: 'Desktop', icon: '🖥️', link: '/desktops' },
    { name: 'Monitors', icon: '📺', link: '/monitors' },
    { name: 'Mini PCs', icon: '📱', link: '/mini-pcs' },
    { name: 'All in One', icon: '🖨️', link: '/all-in-one' },
    { name: 'Accessories', icon: '⌨️', link: '/accessories' },
];

const productBrands = [
    {
        name: 'Laptop', icon: '💻', link: '/laptops',
        brands: [
            { name: 'Lenovo Laptops', link: '/laptops?brand=Lenovo' },
            { name: 'Apple MacBook', link: '/laptops?brand=Apple' },
            { name: 'HP Laptops', link: '/laptops?brand=HP' },
            { name: 'Acer ChromeBook', link: '/laptops?brand=Acer-Chromebook' },
            { name: 'Dell Laptops', link: '/laptops?brand=Dell' },
        ],
    },
    {
        name: 'Desktop', icon: '🖥️', link: '/desktops',
        brands: [
            { name: 'Apple Desktops', link: '/desktops?brand=Apple' },
            { name: 'Lenovo Desktops', link: '/desktops?brand=Lenovo' },
            { name: 'HP Desktops', link: '/desktops?brand=HP' },
            { name: 'Dell Desktops', link: '/desktops?brand=Dell' },
        ],
    },
    {
        name: 'Mini PC', icon: '📱', link: '/mini-pcs',
        brands: [
            { name: 'HP Mini PC', link: '/mini-pcs?brand=HP' },
            { name: 'Dell Mini PC', link: '/mini-pcs?brand=Dell' },
            { name: 'Lenovo Mini PC', link: '/mini-pcs?brand=Lenovo' },
        ],
    },
    {
        name: 'Monitor', icon: '📺', link: '/monitors',
        brands: [
            { name: 'Dell Monitors', link: '/monitors?brand=Dell' },
            { name: 'HP Monitors', link: '/monitors?brand=HP' },
            { name: 'Lenovo Monitors', link: '/monitors?brand=Lenovo' },
        ],
    },
];

const categories = [
    { name: 'Student', icon: '🎓', link: '/laptops?subcategory=Student' },
    { name: 'Office Use', icon: '💼', link: '/laptops?subcategory=Office%20Use' },
    { name: 'Business', icon: '🏢', link: '/laptops?subcategory=Business' },
    { name: 'Gaming', icon: '🎮', link: '/laptops?subcategory=Gaming' },
    { name: 'Creative', icon: '🎨', link: '/laptops?subcategory=Creative' },
    { name: 'Accessories', icon: '⌨️', link: '/laptops?subcategory=Accessories' },
];

const moreItems = [
    { name: 'About Us', desc: 'Learn about our journey', link: '/about-us', icon: 'info' },
    { name: 'Blog', desc: 'Tech insights & guides', link: '/blog', icon: 'blog' },
    { name: 'Business', desc: 'Bulk & corporate orders', link: '/business', icon: 'business' },
    { name: 'Contact Us', desc: 'Get in touch', link: '/contact-us', icon: 'help' },
    { name: 'Warranty', desc: 'Our warranty policy', link: '/warranty', icon: 'warranty' },
    { name: 'Shipping', desc: 'Delivery information', link: '/shipping', icon: 'shipping' },
];

/* SVG icons lookup */
function MoreIcon({ type }) {
    const props = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 };
    switch (type) {
        case 'info': return <svg {...props}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>;
        case 'blog': return <svg {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>;
        case 'business': return <svg {...props}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /></svg>;
        case 'help': return <svg {...props}><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01" /></svg>;
        case 'warranty': return <svg {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
        case 'shipping': return <svg {...props}><rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7V8zM5.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM18.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" /></svg>;
        default: return null;
    }
}

const LottieIcon = React.memo(({ lottieRef, data }) => (
    <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Lottie lottieRef={lottieRef} animationData={data} loop={false} autoplay={false} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
));

import gsap from 'gsap';

const DropArrow = React.memo(() => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.5, flexShrink: 0, display: 'block' }}><path d="M7 10l5 5 5-5z" /></svg>
));

const MobileAccordionItem = ({ isOpen, onToggle, titleContent, children }) => {
    const contentRef = useRef(null);

    useEffect(() => {
        if (!contentRef.current) return;
        if (isOpen) {
            gsap.to(contentRef.current, { height: 'auto', duration: 0.35, ease: 'power2.out', autoAlpha: 1 });
        } else {
            gsap.to(contentRef.current, { height: 0, duration: 0.35, ease: 'power2.inOut', autoAlpha: 0 });
        }
    }, [isOpen]);

    return (
        <>
            <button className="nh-menu-item-v2 nh-menu-trigger-v2" onClick={onToggle}>
                <span className="nh-menu-item-left">
                    {titleContent}
                </span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="2.5" strokeLinecap="round" className={`nh-menu-arrow-v2 ${isOpen ? 'nh-arrow-open' : ''}`}><path d="M6 9l6 6 6-6" /></svg>
            </button>
            <div ref={contentRef} style={{ height: 0, overflow: 'hidden', opacity: 0 }}>
                {children}
            </div>
        </>
    );
};

export default function NewHeader() {
    const { currentUser, logout } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [mobileUserOpen, setMobileUserOpen] = useState(false);
    const hoverCloseTimeoutRef = useRef(null);

    // Lottie refs
    const homeLottieRef = useRef();
    const shopLottieRef = useRef();
    const productsLottieRef = useRef();
    const [moreAnimationDataPublic, setMoreAnimationDataPublic] = useState(null);

    useEffect(() => {
        // try lowercase then capitalized filename
        const tryFetch = async () => {
            const paths = ['/images/icons/more2.json', '/images/icons/More2.json'];
            for (const p of paths) {
                try {
                    const r = await fetch(p);
                    if (!r.ok) continue;
                    const j = await r.json();
                    setMoreAnimationDataPublic(j);
                    return;
                } catch (e) {
                    // continue
                }
            }
            setMoreAnimationDataPublic(null);
        };
        tryFetch();
    }, []);
    const [shopAnimationData, setShopAnimationData] = useState(null);
    const [morePublicData, setMorePublicData] = useState(null);

    useEffect(() => {
        // load the new shopping animation JSON placed in public/images/icons/shopping1.json
        fetch('/images/icons/shopping1.json')
            .then((r) => { if (!r.ok) throw new Error('Failed to fetch shop animation'); return r.json(); })
            .then((j) => setShopAnimationData(j))
            .catch(() => setShopAnimationData(null));
    }, []);

    useEffect(() => {
        // attempt to load a public 'more1.json' to override the bundled more.json
        fetch('/images/icons/more1.json')
            .then((r) => { if (!r.ok) throw new Error('no public more1'); return r.json(); })
            .then((j) => setMorePublicData(j))
            .catch(() => setMorePublicData(null));
    }, []);
    const categoriesLottieRef = useRef();
    const storeLottieRef = useRef();
    const aboutLottieRef = useRef();
    const blogLottieRef = useRef();
    const businessLottieRef = useRef();
    const moreLottieRef = useRef();

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', handler, { passive: true });
        return () => window.removeEventListener('scroll', handler);
    }, []);

    useEffect(() => {
        if (mobileOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    useEffect(() => {
        return () => {
            if (hoverCloseTimeoutRef.current) {
                clearTimeout(hoverCloseTimeoutRef.current);
                hoverCloseTimeoutRef.current = null;
            }
        };
    }, []);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e) => {
            // Do not close if clicking inside a mobile slide overlay, since it handles its own accordion toggles
            if (!e.target.closest('.nh-dropdown-container') && !e.target.closest('.nh-mobile-slide-v2')) {
                setOpenDropdown(null);
            }
            if (!e.target.closest('.nh-mob-user-wrapper')) {
                setMobileUserOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const clearHoverCloseTimeout = () => {
        if (hoverCloseTimeoutRef.current) {
            clearTimeout(hoverCloseTimeoutRef.current);
            hoverCloseTimeoutRef.current = null;
        }
    };

    const handleLinkClick = () => {
        clearHoverCloseTimeout();
        setMobileOpen(false);
        setOpenDropdown(null);
        setSearchOpen(false);
        setMobileUserOpen(false);
    };

    const handleLogout = async () => {
        try {
            await logout();
            handleLinkClick();
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            window.location.href = `/laptops?search=${encodeURIComponent(searchQuery)}`;
            setSearchOpen(false);
            setMobileOpen(false);
        }
    };

    const handleMobileAccountClick = () => {
        clearHoverCloseTimeout();
        setSearchOpen(false);
        setMobileUserOpen((prev) => !prev);
    };

    const toggleDropdown = (key) => {
        clearHoverCloseTimeout();
        setOpenDropdown(prev => prev === key ? null : key);
    };

    const handleMouseEnter = (key) => {
        if (window.innerWidth <= 768) return;
        clearHoverCloseTimeout();
        setOpenDropdown(key);
    };

    const handleMouseLeave = () => {
        if (window.innerWidth <= 768) return;
        clearHoverCloseTimeout();
        hoverCloseTimeoutRef.current = setTimeout(() => {
            setOpenDropdown(null);
            hoverCloseTimeoutRef.current = null;
        }, 150);
    };

    const playLottie = (ref, dir = 1) => {
        if (ref.current) {
            if (dir === 1) {
                ref.current.stop();
                ref.current.setDirection(1);
                ref.current.play();
            } else {
                ref.current.setDirection(-1);
                ref.current.play();
            }
        }
    };

    const location = useLocation();
    const loginState = { from: location };
    const isHomePage = location.pathname === '/';
    const isCartPage = location.pathname.startsWith('/cart');
    const isCheckoutPage = location.pathname.startsWith('/checkout');
    const isOrdersPage = location.pathname.startsWith('/orders');
    const isOrderConfirmationPage = location.pathname.startsWith('/order-confirmation');
    const isAdminAnalyticsPage = location.pathname.startsWith('/admin/analytics');
    const isLoginPage = location.pathname.startsWith('/login');
    const isProductPage = location.pathname.match(/^\/(product|products|monitor|desktop|mini-pc|all-in-one-pc|accessory)(\/|$)/);
    const isBlogPage = location.pathname.startsWith('/blog');

    return (
        <>
            {/* ─── HEADER BAR ─── */}
            <header
                className={`fixed left-0 right-0 z-[999] transition-all duration-500 ${(scrolled || isCartPage || isCheckoutPage || isOrdersPage || isOrderConfirmationPage || isAdminAnalyticsPage || isLoginPage || isProductPage || isBlogPage) ? 'top-0 bg-evergreen-deep/95 backdrop-blur-xl border-b border-white/10 py-1' : (isHomePage ? 'top-[var(--tb-h,36px)]' : 'top-0') + ' bg-transparent py-3'} ${isLoginPage ? 'bg-evergreen-deep/95' : ''}`}
                style={{ fontFamily: FONT }}
            >
                {/* ─── DESKTOP HEADER LAYOUT (unchanged) ─── */}
                <div className="nh-desktop-bar max-w-7xl mx-auto px-4 md:px-6 items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="no-underline" onClick={handleLinkClick} style={{ display: 'flex', alignItems: 'center' }}>
                        <img src="/images/nexawave-logo.png" alt="Nexawave Technology - Premium Refurbished Electronics" style={{ height: 40, objectFit: 'contain' }} />
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-1">
                        {/* Home */}
                        <Link to="/" className="nh-nav-link" onClick={handleLinkClick}
                            onMouseEnter={() => playLottie(homeLottieRef)} onMouseLeave={() => playLottie(homeLottieRef, -1)}>
                            <LottieIcon lottieRef={homeLottieRef} data={homeAnimationData} />
                            <FlipText text="Home" className="nh-label" frontColor="#ffffff" backColor="#000000" />
                        </Link>

                        {/* Shop (dropdown) */}
                        <div className="nh-dropdown-container" style={{ position: 'relative' }}
                            onMouseEnter={() => handleMouseEnter('shop')} onMouseLeave={handleMouseLeave}>
                            <button className="nh-nav-link" onClick={() => toggleDropdown('shop')}
                                onMouseEnter={() => playLottie(shopLottieRef)} onMouseLeave={() => playLottie(shopLottieRef, -1)}>
                                {shopAnimationData ? (
                                    <LottieIcon lottieRef={shopLottieRef} data={shopAnimationData} />
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0 }}>
                                        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                        <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
                                    </svg>
                                )}
                                <FlipText text="Shop" className="nh-label" frontColor="#ffffff" backColor="#000000" />
                                <DropArrow />
                            </button>
                            {openDropdown === 'shop' && (
                                <div className="nh-dropdown" onMouseEnter={() => handleMouseEnter('shop')} onMouseLeave={handleMouseLeave}>
                                    <div className="nh-dropdown-header">
                                        <h4>Find Your Next Device</h4>
                                        <p>Discover premium refurbished technology</p>
                                    </div>
                                    <div className="nh-dropdown-grid">
                                        {deviceTypes.map((d, i) => (
                                            <Link key={i} to={d.link} className="nh-dropdown-item" onClick={handleLinkClick}>
                                                <span className="nh-item-icon">{d.icon}</span>
                                                <span className="nh-item-name">{d.name}</span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Products (dropdown) */}
                        <div className="nh-dropdown-container" style={{ position: 'relative' }}
                            onMouseEnter={() => handleMouseEnter('products')} onMouseLeave={handleMouseLeave}>
                            <button className="nh-nav-link" onClick={() => toggleDropdown('products')}
                                onMouseEnter={() => playLottie(productsLottieRef)} onMouseLeave={() => playLottie(productsLottieRef, -1)}>
                                <LottieIcon lottieRef={productsLottieRef} data={productsAnimationData} />
                                <FlipText text="Products" className="nh-label" frontColor="#ffffff" backColor="#000000" />
                                <DropArrow />
                            </button>
                            {openDropdown === 'products' && (
                                <div className="nh-dropdown nh-dropdown-wide" onMouseEnter={() => handleMouseEnter('products')} onMouseLeave={handleMouseLeave}>
                                    <div className="nh-dropdown-header">
                                        <h4>Explore Products by Brand</h4>
                                        <p>Find your preferred brand and device type</p>
                                    </div>
                                    <div className="nh-products-grid">
                                        {productBrands.map((pt, i) => (
                                            <div key={i} className="nh-product-col">
                                                <Link to={pt.link} className="nh-product-type-title" onClick={handleLinkClick}>
                                                    <span>{pt.icon}</span> {pt.name}
                                                </Link>
                                                <ul className="nh-brands-list">
                                                    {pt.brands.map((b, j) => (
                                                        <li key={j}><Link to={b.link} onClick={handleLinkClick}>{b.name}</Link></li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Categories (dropdown) */}
                        <div className="nh-dropdown-container" style={{ position: 'relative' }}
                            onMouseEnter={() => handleMouseEnter('categories')} onMouseLeave={handleMouseLeave}>
                            <button className="nh-nav-link" onClick={() => toggleDropdown('categories')}
                                onMouseEnter={() => playLottie(categoriesLottieRef)} onMouseLeave={() => playLottie(categoriesLottieRef, -1)}>
                                <LottieIcon lottieRef={categoriesLottieRef} data={categoriesAnimationData} />
                                <FlipText text="Categories" className="nh-label" frontColor="#ffffff" backColor="#000000" />
                                <DropArrow />
                            </button>
                            {openDropdown === 'categories' && (
                                <div className="nh-dropdown" onMouseEnter={() => handleMouseEnter('categories')} onMouseLeave={handleMouseLeave}>
                                    <div className="nh-dropdown-header">
                                        <h4>Shop by Category</h4>
                                        <p>Tailored solutions for every use case</p>
                                    </div>
                                    <div className="nh-dropdown-grid">
                                        {categories.map((c, i) => (
                                            <Link key={i} to={c.link} className="nh-dropdown-item" onClick={handleLinkClick}>
                                                <span className="nh-item-icon">{c.icon}</span>
                                                <span className="nh-item-name">{c.name}</span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Store */}
                        <Link to="/store" className="nh-nav-link" onClick={handleLinkClick}
                            onMouseEnter={() => playLottie(storeLottieRef)} onMouseLeave={() => playLottie(storeLottieRef, -1)}>
                            <LottieIcon lottieRef={storeLottieRef} data={storeAnimationData} />
                            <FlipText text="Store" className="nh-label" frontColor="#ffffff" backColor="#000000" />
                        </Link>

                        {/* More (dropdown) */}
                        <div className="nh-dropdown-container" style={{ position: 'relative' }}
                            onMouseEnter={() => handleMouseEnter('more')} onMouseLeave={handleMouseLeave}>
                            <button className="nh-nav-link" onClick={() => toggleDropdown('more')}
                                onMouseEnter={() => playLottie(moreLottieRef)} onMouseLeave={() => playLottie(moreLottieRef, -1)}>
                                {(morePublicData || moreAnimationDataPublic || moreAnimationData) ? (
                                    <LottieIcon lottieRef={moreLottieRef} data={morePublicData || moreAnimationDataPublic || moreAnimationData} />
                                ) : (
                                    <div style={{ width: 32, height: 32, display: 'block', flexShrink: 0 }} />
                                )}
                                <FlipText text="More" className="nh-label" frontColor="#ffffff" backColor="#000000" />
                                <DropArrow />
                            </button>
                            {openDropdown === 'more' && (
                                <div className="nh-dropdown nh-dropdown-right" onMouseEnter={() => handleMouseEnter('more')} onMouseLeave={handleMouseLeave}>
                                    <div className="nh-dropdown-header">
                                        <h4>More Options</h4>
                                        <p>Additional resources and support</p>
                                    </div>
                                    <div className="nh-more-list">
                                        {moreItems.map((m, i) => (
                                            <Link key={i} to={m.link} className="nh-more-item" onClick={handleLinkClick}>
                                                <div className="nh-more-icon"><MoreIcon type={m.icon} /></div>
                                                <div className="nh-more-info">
                                                    <span className="nh-more-title">{m.name}</span>
                                                    <span className="nh-more-desc">{m.desc}</span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </nav>

                    {/* Right side: action icons (desktop) */}
                    <div className="nh-actions-group">
                        {/* Search */}
                        <button className="nh-action-btn" onClick={() => setSearchOpen(!searchOpen)} title="Search">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                            </svg>
                        </button>

                        {/* Cart */}
                        <Link to="/cart" className="nh-action-btn" title="Cart" onClick={handleLinkClick}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
                            </svg>
                        </Link>

                        {/* Login / Account Dropdown */}
                        {currentUser ? (
                            <div className="nh-dropdown-container" style={{ position: 'relative' }}
                                onMouseEnter={() => handleMouseEnter('user')} onMouseLeave={handleMouseLeave}>
                                <button className="nh-action-btn nh-user-btn" title="Account" onClick={() => toggleDropdown('user')}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                        <circle cx="12" cy="8" r="5" /><path d="M20 21a8 8 0 00-16 0" />
                                    </svg>
                                </button>
                                {openDropdown === 'user' && (
                                    <div className="nh-dropdown nh-dropdown-user nh-dropdown-right" onMouseEnter={() => handleMouseEnter('user')} onMouseLeave={handleMouseLeave}>
                                        <div className="nh-dropdown-header nh-user-header">
                                            <div className="nh-user-name">{currentUser.displayName || 'My Account'}</div>
                                            <div className="nh-user-phone">{currentUser.phoneNumber}</div>
                                        </div>
                                        <div className="nh-more-list nh-user-list">
                                            <Link to="/orders" className="nh-more-item" onClick={handleLinkClick}>
                                                <div className="nh-more-icon">📦</div>
                                                <div className="nh-more-info"><span className="nh-more-title">My Orders</span></div>
                                            </Link>
                                            <Link to="/profile" className="nh-more-item" onClick={handleLinkClick}>
                                                <div className="nh-more-icon">👤</div>
                                                <div className="nh-more-info"><span className="nh-more-title">Profile</span></div>
                                            </Link>
                                            <Link to="/settings" className="nh-more-item" onClick={handleLinkClick}>
                                                <div className="nh-more-icon">⚙️</div>
                                                <div className="nh-more-info"><span className="nh-more-title">Settings</span></div>
                                            </Link>
                                            <hr className="nh-divider" />
                                            <button className="nh-more-item nh-logout-btn" onClick={handleLogout}>
                                                <div className="nh-more-icon">🚪</div>
                                                <div className="nh-more-info"><span className="nh-more-title">Logout</span></div>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link to="/login" state={loginState} className="nh-action-btn" title="Login" onClick={handleLinkClick}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <circle cx="12" cy="8" r="5" /><path d="M20 21a8 8 0 00-16 0" />
                                </svg>
                            </Link>
                        )}
                    </div>
                </div>

                {/* ─── MOBILE HEADER BAR ─── */}
                {/* Layout: [hamburger] [search] ... [LOGO centered] ... [cart] [login] */}
                <div className="nh-mobile-bar">
                    {/* LEFT: hamburger + search */}
                    <div className="nh-mob-left">
                        <button className="nh-mob-icon-btn"
                            onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
                            {mobileOpen ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <path d="M3 6h18M3 12h18M3 18h18" />
                                </svg>
                            )}
                        </button>
                        <button className="nh-mob-icon-btn" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                            </svg>
                        </button>
                    </div>

                    {/* CENTER: Logo (slightly smaller) */}
                    <Link to="/" className="nh-mob-logo-link" onClick={handleLinkClick}>
                        <img src="/images/nexawave-logo.png" alt="Nexawave Technology" className="nh-mob-logo" />
                    </Link>

                    {/* RIGHT: cart + login */}
                    <div className="nh-mob-right">
                        <Link to="/cart" className="nh-mob-icon-btn" onClick={handleLinkClick} aria-label="Cart">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
                            </svg>
                        </Link>
                        {currentUser ? (
                            <div className="nh-mob-user-wrapper">
                                <button className="nh-mob-icon-btn nh-mob-user-active" onClick={handleMobileAccountClick} aria-label="Account">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <circle cx="12" cy="8" r="4" /><path d="M20 21a8 8 0 00-16 0" />
                                    </svg>
                                </button>
                                {mobileUserOpen && (
                                    <div className="nh-mob-user-popover">
                                        <div className="nh-mob-user-header">
                                            <div className="nh-mob-user-name">{currentUser.displayName || 'My Account'}</div>
                                            <div className="nh-mob-user-phone">{currentUser.phoneNumber}</div>
                                        </div>
                                        <Link to="/orders" className="nh-mob-user-item" onClick={handleLinkClick}>
                                            My Orders
                                        </Link>
                                        <Link to="/profile" className="nh-mob-user-item" onClick={handleLinkClick}>
                                            Profile
                                        </Link>
                                        <Link to="/settings" className="nh-mob-user-item" onClick={handleLinkClick}>
                                            Settings
                                        </Link>
                                        <button className="nh-mob-user-item nh-mob-user-logout" onClick={handleLogout}>
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link to="/login" state={loginState} className="nh-mob-icon-btn" onClick={handleLinkClick} aria-label="Login">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <circle cx="12" cy="8" r="4" /><path d="M20 21a8 8 0 00-16 0" />
                                </svg>
                            </Link>
                        )}
                    </div>
                </div>

                {/* Search bar slide-down */}
                {searchOpen && (
                    <div className="nh-search-bar">
                        <form onSubmit={handleSearch} className="max-w-2xl mx-auto flex items-center gap-2 px-4">
                            <input
                                type="text"
                                placeholder="Search laptops, brands, models..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                                className="nh-search-input"
                            />
                            <button type="submit" className="nh-search-submit">Search</button>
                            <button type="button" className="nh-search-close" onClick={() => setSearchOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
                            </button>
                        </form>
                    </div>
                )}
            </header>

            {/* ─── MOBILE MENU OVERLAY ─── */}
            {mobileOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, fontFamily: FONT }}>
                    {/* Backdrop */}
                    <div onClick={() => setMobileOpen(false)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 1 }} />
                    {/* Slide panel - LEFT side, white bg */}
                    <div className="nh-mobile-slide-v2" style={{ position: 'absolute', top: 0, left: 0, width: '85%', maxWidth: '384px', height: '100%', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', zIndex: 2 }}>

                        {/* ─── SCROLLABLE AREA (everything except fixed bottom) ─── */}
                        <div className="nh-mobile-scroll-area" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>

                            {/* Menu Header with Close Button */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 16px 8px' }}>
                                <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1a1a', letterSpacing: '0.05em', fontFamily: 'Oswald, sans-serif' }}>MENU</span>
                                <button onClick={() => setMobileOpen(false)} aria-label="Close menu" style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                </button>
                            </div>

                            {/* ─── Top: 3 Product Image Cards ─── */}
                            <div className="nh-menu-cards-row" style={{ paddingTop: 8 }}>
                                <Link to="/laptops?brand=Lenovo" className="nh-menu-card" onClick={handleLinkClick}>
                                    <div className="nh-menu-card-img">
                                        <img src="/images/menu/menu1.png" alt="Lenovo Laptops" />
                                    </div>
                                    <span className="nh-menu-card-label">LENOVO LAPTOPS</span>
                                </Link>
                                <Link to="/laptops?brand=Apple" className="nh-menu-card" onClick={handleLinkClick}>
                                    <div className="nh-menu-card-img">
                                        <img src="/images/menu/menu2.png" alt="Apple MacBooks" />
                                    </div>
                                    <span className="nh-menu-card-label">APPLE MACBOOKS</span>
                                </Link>
                                <Link to="/desktops" className="nh-menu-card" onClick={handleLinkClick}>
                                    <div className="nh-menu-card-img">
                                        <img src="/images/menu/menu3.png" alt="Desktops" />
                                    </div>
                                    <span className="nh-menu-card-label">DESKTOPS</span>
                                </Link>
                            </div>

                            {/* ─── Promo Banner ─── */}
                            <div className="nh-menu-promo-banner" style={{ background: '#f0f9ff', border: '1px solid rgba(0, 99, 184, 0.1)' }}>
                                <div className="nh-promo-inner">
                                    <span className="nh-promo-tag" style={{ color: '#0063B8' }}>LAPTOPS</span>
                                    <span className="nh-promo-deal" style={{ color: '#0063B8' }}>STARTING AT JUST ₹5,999</span>
                                    <Link to="/laptops" className="nh-promo-cta" style={{ background: '#0063B8', color: '#fff' }} onClick={handleLinkClick}>SHOP LAPTOPS</Link>
                                </div>
                            </div>

                            {/* ─── Mobile search bar ─── */}
                            {searchOpen && (
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                                    <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <input
                                            type="text"
                                            placeholder="Search laptops..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            autoFocus
                                            className="nh-mobile-search-input-v2"
                                        />
                                        <button type="submit" className="nh-mobile-search-btn-v2">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                                        </button>
                                    </form>
                                </div>
                            )}

                            {/* ─── Nav items ─── */}
                            <div className="nh-menu-nav-v2">
                                {/* Home */}
                                <Link to="/" className="nh-menu-item-v2" onClick={handleLinkClick}>
                                    <span className="nh-menu-item-left"><LottieIcon lottieRef={homeLottieRef} data={homeAnimationData} /> Home</span>
                                </Link>

                                {/* Shop */}
                                <MobileAccordionItem 
                                    isOpen={openDropdown === 'shop'} 
                                    onToggle={() => toggleDropdown('shop')} 
                                    titleContent={<>{shopAnimationData ? <LottieIcon lottieRef={shopLottieRef} data={shopAnimationData} /> : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0 }}>
                                            <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
                                        </svg>
                                    )} Shop</>}
                                >
                                    <div className="nh-menu-sub-v2">
                                        {deviceTypes.map((d, i) => (
                                            <Link key={i} to={d.link} className="nh-menu-sub-link-v2" onClick={handleLinkClick}>
                                                <span>{d.icon}</span> {d.name}
                                            </Link>
                                        ))}
                                    </div>
                                </MobileAccordionItem>

                                {/* Products */}
                                <MobileAccordionItem 
                                    isOpen={openDropdown === 'products'} 
                                    onToggle={() => toggleDropdown('products')} 
                                    titleContent={<><LottieIcon lottieRef={productsLottieRef} data={productsAnimationData} /> Products</>}
                                >
                                    <div className="nh-menu-sub-v2">
                                        {productBrands.map((pt, i) => (
                                            <div key={i} style={{ marginBottom: 8 }}>
                                                <Link to={pt.link} className="nh-menu-sub-title-v2" onClick={handleLinkClick}>{pt.icon} {pt.name}</Link>
                                                {pt.brands.map((b, j) => (
                                                    <Link key={j} to={b.link} className="nh-menu-sub-link-v2 nh-sub-indent-v2" onClick={handleLinkClick}>{b.name}</Link>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </MobileAccordionItem>

                                {/* Categories */}
                                <MobileAccordionItem 
                                    isOpen={openDropdown === 'categories'} 
                                    onToggle={() => toggleDropdown('categories')} 
                                    titleContent={<><LottieIcon lottieRef={categoriesLottieRef} data={categoriesAnimationData} /> Categories</>}
                                >
                                    <div className="nh-menu-sub-v2">
                                        {categories.map((c, i) => (
                                            <Link key={i} to={c.link} className="nh-menu-sub-link-v2" onClick={handleLinkClick}>
                                                <span>{c.icon}</span> {c.name}
                                            </Link>
                                        ))}
                                    </div>
                                </MobileAccordionItem>

                                {/* Store */}
                                <Link to="/store" className="nh-menu-item-v2" onClick={handleLinkClick}>
                                    <span className="nh-menu-item-left"><LottieIcon lottieRef={storeLottieRef} data={storeAnimationData} /> Store</span>
                                </Link>

                                {/* More */}
                                <MobileAccordionItem 
                                    isOpen={openDropdown === 'more'} 
                                    onToggle={() => toggleDropdown('more')} 
                                    titleContent={<>{(morePublicData || moreAnimationDataPublic || moreAnimationData) ? <LottieIcon lottieRef={moreLottieRef} data={morePublicData || moreAnimationDataPublic || moreAnimationData} /> : <div style={{ width: 32, height: 32, display: 'block', flexShrink: 0 }} />} More</>}
                                >
                                    <div className="nh-menu-sub-v2">
                                        {moreItems.map((m, i) => (
                                            <Link key={i} to={m.link} className="nh-menu-sub-link-v2" onClick={handleLinkClick}>
                                                <MoreIcon type={m.icon} /> {m.name}
                                            </Link>
                                        ))}
                                    </div>
                                </MobileAccordionItem>

                                {/* Account (in-menu, not in the fixed footer) */}
                                {currentUser ? (
                                    <MobileAccordionItem 
                                        isOpen={openDropdown === 'user'} 
                                        onToggle={() => toggleDropdown('user')} 
                                        titleContent={<>
                                            <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M20 21a8 8 0 00-16 0" /></svg>
                                            </div>
                                            Account
                                        </>}
                                    >
                                        <div className="nh-menu-sub-v2">
                                            <Link to="/profile" className="nh-menu-sub-link-v2" onClick={handleLinkClick}>My Profile</Link>
                                            <Link to="/orders" className="nh-menu-sub-link-v2" onClick={handleLinkClick}>My Orders</Link>
                                            <Link to="/wishlist" className="nh-menu-sub-link-v2" onClick={handleLinkClick}>Wishlist</Link>
                                            <button onClick={() => { logout(); setMobileOpen(false); }} className="nh-menu-sub-link-v2" style={{ cursor: 'pointer', width: '100%', textAlign: 'left', background: 'none', borderBottom: 'none' }}>
                                                Logout
                                            </button>
                                        </div>
                                    </MobileAccordionItem>
                                ) : (
                                    <Link to="/login" state={loginState} className="nh-menu-item-v2" onClick={handleLinkClick}>
                                        <span className="nh-menu-item-left">
                                            <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M20 21a8 8 0 00-16 0" /></svg>
                                            </div>
                                            Login
                                        </span>
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* ─── FIXED BOTTOM: Track + Account buttons + Follow Us ─── */}
                        <div className="nh-menu-fixed-bottom">
                            {/* Track Order + Log In buttons */}
                            <div className="nh-menu-btns-row">
                                <Link to="/orders" className="nh-menu-btn-outline" onClick={handleLinkClick}>
                                    TRACK ORDER
                                </Link>
                                {currentUser ? (
                                    <Link to="/profile" className="nh-menu-btn-outline" onClick={handleLinkClick}>
                                        ACCOUNT
                                    </Link>
                                ) : (
                                    <Link to="/login" state={loginState} className="nh-menu-btn-outline" onClick={handleLinkClick}>
                                        LOG IN
                                    </Link>
                                )}
                            </div>
                            {/* Follow Us */}
                            <div className="nh-menu-follow">
                                <span className="nh-menu-follow-label">FOLLOW US</span>
                                <div className="nh-menu-follow-icons">
                                    {/* Instagram */}
                                    <a href="https://www.instagram.com/nexawavechaderghat/" target="_blank" rel="noopener noreferrer" className="nh-menu-social-icon" aria-label="Instagram">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
                                    </a>
                                    {/* YouTube */}
                                    <a href="https://www.youtube.com/@nexawavetechnology-u6o" target="_blank" rel="noopener noreferrer" className="nh-menu-social-icon" aria-label="YouTube">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                                    </a>
                                    {/* Facebook */}
                                    <a href="https://www.facebook.com/profile.php?id=61577081417375" target="_blank" rel="noopener noreferrer" className="nh-menu-social-icon" aria-label="Facebook">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
